import { hasAnalyticsConsent } from "@/lib/consent";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

interface TrackEventParams {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

export function trackEvent({ action, category, label, value }: TrackEventParams): void {
  if (typeof window === "undefined") return;
  if (!hasAnalyticsConsent()) return;
  if (!window.gtag) return;

  window.gtag("event", action, {
    event_category: category,
    event_label: label,
    value,
  });
}

/**
 * Fire a standard GA4 event with arbitrary parameters.
 * Use this for GA4 recommended events (sign_up, purchase, begin_checkout, etc.)
 * that require specific parameter shapes Google Ads can import as conversions.
 */
export function trackGA4Event(eventName: string, params?: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  if (!hasAnalyticsConsent()) return;
  if (!window.gtag) return;
  window.gtag("event", eventName, params);
}

/**
 * Direct Google Ads conversion labels (send_to values).
 *
 * These bypass the GA4 → Google Ads import pipeline, which has proven unreliable
 * (0 conversions imported despite correct configuration on both sides).
 * Direct tags fire conversion data to Google Ads in real-time.
 *
 * To add a new label: create a "Website" conversion action in Google Ads UI
 * (Goals → Conversions → New → Website → Manual), then copy the send_to value.
 */
const GOOGLE_ADS_LABELS: Record<string, string> = {
  sign_up: "AW-18025620863/SDetCNqjirAcEP_KpJND",
  generate_lead: "AW-18025620863/0vCCCN2jirAcEP_KpJND",
};

/**
 * Fire a conversion event that ALWAYS calls gtag, regardless of cookie consent.
 *
 * Consent Mode v2 (configured in Analytics.tsx) controls what data Google can
 * actually store. When consent is denied, Google still receives a "cookieless
 * ping" used for conversion modeling. NOT calling gtag means zero data — not
 * even modeled conversions.
 *
 * Use this for business-critical conversion events: sign_up, generate_lead,
 * credential_created, purchase, begin_checkout.
 *
 * Also fires a direct Google Ads conversion tag (via send_to) if a label exists
 * for the event. This bypasses the broken GA4 import pipeline.
 */
export function trackConversion(eventName: string, params?: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  if (!window.gtag) return;

  // Fire the GA4 event (always — consent mode handles privacy)
  window.gtag("event", eventName, params);

  // Fire direct Google Ads conversion tag if we have a label
  const label = GOOGLE_ADS_LABELS[eventName];
  if (label) {
    window.gtag("event", "conversion", {
      send_to: label,
      value: (params as Record<string, unknown> | undefined)?.value,
      currency: (params as Record<string, unknown> | undefined)?.currency ?? "USD",
    });
  }
}
